import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Check, ChevronDown, Moon, Zap } from 'lucide-react';
import { useDashboardStore } from '@/features/dashboard';
import { isEnvironmentPinned } from '@/config/environments';
import { cn } from '@/lib/utils';

interface EnvironmentSelectorProps {
  className?: string;
}

export function EnvironmentSelector({ className }: EnvironmentSelectorProps) {
  const { currentEnvironment, environments, switchEnvironment } = useDashboardStore();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuWidth, setMenuWidth] = useState<number>();

  const getEnvironmentIcon = (isProduction: boolean) => {
    return isProduction ? <Zap className="h-4 w-4 shrink-0" /> : <Moon className="h-4 w-4 shrink-0" />;
  };

  const getEnvironmentBadge = (isProduction: boolean) => {
    return (
      <Badge 
        variant={isProduction ? 'destructive' : 'secondary'}
        className={`text-xs font-medium shrink-0 ${
          isProduction 
            ? 'bg-destructive/10 text-destructive border-destructive/20' 
            : 'bg-primary/10 text-primary border-primary/20'
        }`}
      >
        {isProduction ? 'PROD' : 'STAGING'}
      </Badge>
    );
  };

  const getEnvironmentName = (name: string) => {
    // Remove "Environment" from the name to avoid redundancy
    return name.replace(' Environment', '');
  };

  // Deployed staging/production builds are pinned — show badge only, no switcher
  if (isEnvironmentPinned || environments.length <= 1) {
    return (
      <Button
        ref={triggerRef}
        variant="outline"
        disabled
        className={cn(
          'h-9 w-full md:w-auto md:min-w-[180px] justify-start gap-2 px-3 py-2 text-xs md:text-sm opacity-100',
          className
        )}
      >
        {getEnvironmentIcon(currentEnvironment.isProduction)}
        <span className="font-medium text-foreground truncate">
          {getEnvironmentName(currentEnvironment.name)}
        </span>
        <span className="hidden md:inline-flex">
          {getEnvironmentBadge(currentEnvironment.isProduction)}
        </span>
      </Button>
    );
  }

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open && triggerRef.current) {
          setMenuWidth(triggerRef.current.offsetWidth);
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button 
          ref={triggerRef}
          variant="outline" 
          className={cn(
            'h-9 w-full md:w-auto md:min-w-[180px] justify-start gap-2 px-3 py-2 hover:bg-accent/50 transition-colors text-xs md:text-sm',
            className
          )}
        >
          {getEnvironmentIcon(currentEnvironment.isProduction)}
          <span className="font-medium text-foreground truncate">
            {getEnvironmentName(currentEnvironment.name)}
          </span>
          <span className="hidden md:inline-flex">
            {getEnvironmentBadge(currentEnvironment.isProduction)}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground ml-auto" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="p-1"
        style={menuWidth ? { width: menuWidth } : undefined}
      >
        {environments.map((env) => {
          return (
            <DropdownMenuItem
              key={env.id}
              onClick={() => switchEnvironment(env.id)}
              className="flex items-center justify-between gap-2 px-3 py-2 rounded-sm cursor-pointer hover:bg-accent/50"
            >
              <div className="flex items-center gap-2 min-w-0">
                {getEnvironmentIcon(env.isProduction)}
                <span className="text-sm font-medium truncate">
                  {getEnvironmentName(env.name)}
                </span>
                <span className="hidden md:inline-flex">
                  {getEnvironmentBadge(env.isProduction)}
                </span>
              </div>
              {currentEnvironment.id === env.id && (
                <Check className="h-4 w-4 shrink-0 text-primary" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
